import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Switch,
  Stack,
  Button,
  Tabs,
  Tab,
} from "@mui/material";
import { Add, Delete, ArrowUpward, ArrowDownward } from "@mui/icons-material";
import { toast } from "react-toastify";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../constants/permissions";
import {
  ActiveDashboardWidget,
  ALL_DASHBOARD_WIDGETS,
  DashboardKey,
  getDashboardStorageKey,
  getDefaultWidgetsForDashboard,
} from "./DashboardWidgetsConfig";

const DASHBOARDS: { key: DashboardKey; label: string }[] = [
  { key: "sales", label: "Sales Dashboard" },
  { key: "operations", label: "Operations Dashboard" },
   { key: "tv", label: "TV Dashboard" },
];

export default function DashboardWidgetsAdmin() {
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(PERMISSIONS.CONFIGURATION_CHANGE);
  const canAdd = hasPermission(PERMISSIONS.CONFIGURATION_ADD);
  const canDelete = hasPermission(PERMISSIONS.CONFIGURATION_DELETE);
  const [dashboardKey, setDashboardKey] = useState<DashboardKey>("sales");
  const [activeWidgets, setActiveWidgets] = useState<ActiveDashboardWidget[]>(
    [],
  );

  const storageKey = getDashboardStorageKey(dashboardKey);

  useEffect(() => {
    const saved = localStorage.getItem(storageKey);

    if (saved) {
      setActiveWidgets(JSON.parse(saved));
    } else {
      setActiveWidgets(getDefaultWidgetsForDashboard(dashboardKey));
    }
  }, [dashboardKey, storageKey]);

  const allowedWidgets = useMemo(() => {
    return ALL_DASHBOARD_WIDGETS.filter((widget) =>
      widget.dashboards.includes(dashboardKey),
    );
  }, [dashboardKey]);

  const availableWidgets = allowedWidgets.filter(
    (widget) => !activeWidgets.some((active) => active.id === widget.id),
  );

  const saveWidgets = () => {
    localStorage.setItem(storageKey, JSON.stringify(activeWidgets));
    toast.success("Dashboard widgets saved successfully");
  };

  const addWidget = (id: string) => {
    const widgetInfo = ALL_DASHBOARD_WIDGETS.find((widget) => widget.id === id);
    if (!widgetInfo) return;

    setActiveWidgets((prev) => [
      ...prev,
      {
        id,
        visible: true,
        slot: widgetInfo.defaultSlot,
      },
    ]);
  };

  const removeWidget = (id: string) => {
    setActiveWidgets((prev) => prev.filter((widget) => widget.id !== id));
  };

  const toggleVisible = (id: string) => {
    setActiveWidgets((prev) =>
      prev.map((widget) =>
        widget.id === id
          ? {
              ...widget,
              visible: !widget.visible,
            }
          : widget,
      ),
    );
  };
  const changeSize = (id: string, md: number) => {
    setActiveWidgets((prev) =>
      prev.map((widget) =>
        widget.id === id && widget.slot.type === "main_grid"
          ? {
              ...widget,
              slot: {
                ...widget.slot,
                size: { ...widget.slot.size, md },
              },
            }
          : widget,
      ),
    );
  };

  const moveUp = (id: string) => {
    setActiveWidgets((prev) => {
      const index = prev.findIndex((widget) => widget.id === id);
      if (index <= 0) return prev;

      const copy = [...prev];
      [copy[index - 1], copy[index]] = [copy[index], copy[index - 1]];
      return copy;
    });
  };

  const moveDown = (id: string) => {
    setActiveWidgets((prev) => {
      const index = prev.findIndex((widget) => widget.id === id);
      if (index === -1 || index === prev.length - 1) return prev;

      const copy = [...prev];
      [copy[index], copy[index + 1]] = [copy[index + 1], copy[index]];
      return copy;
    });
  };

  const resetDashboard = () => {
    const defaults = getDefaultWidgetsForDashboard(dashboardKey);
    setActiveWidgets(defaults);
    localStorage.setItem(storageKey, JSON.stringify(defaults));
    toast.success("Dashboard reset to default widgets");
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard Widgets Administration
      </Typography>

      <Typography variant="body2" sx={{ mb: 3 }}>
        Choose which widgets should display on each dashboard.
      </Typography>

      <Tabs
        value={dashboardKey}
        onChange={(_, newValue) => setDashboardKey(newValue as DashboardKey)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          mb: 3,
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 600,
          },
        }}
      >
        {DASHBOARDS.map((dashboard) => (
          <Tab
            key={dashboard.key}
            label={dashboard.label}
            value={dashboard.key}
          />
        ))}
      </Tabs>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">Active Widgets</Typography>

            <Stack spacing={2} sx={{ mt: 2 }}>
              {activeWidgets.map((activeWidget, index) => {
                const widgetInfo = ALL_DASHBOARD_WIDGETS.find(
                  (widget) => widget.id === activeWidget.id,
                );

                if (!widgetInfo) return null;

                return (
                  <Paper key={activeWidget.id} sx={{ p: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 2,
                      }}
                    >
                      <Box>
                        <Typography fontWeight={700}>
                          {widgetInfo.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {widgetInfo.description}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {canEdit && (
                          <>
                            <IconButton
                              size="small"
                              disabled={index === 0}
                              onClick={() => moveUp(activeWidget.id)}
                            >
                              <ArrowUpward fontSize="small" />
                            </IconButton>

                            <IconButton
                              size="small"
                              disabled={index === activeWidgets.length - 1}
                              onClick={() => moveDown(activeWidget.id)}
                            >
                              <ArrowDownward fontSize="small" />
                            </IconButton>
                          </>
                        )}

                        {canDelete && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeWidget(activeWidget.id)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mt: 2,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography variant="body2">Visible</Typography>
                        <Switch
                          checked={activeWidget.visible}
                          disabled={!canEdit}
                          onChange={() => toggleVisible(activeWidget.id)}
                        />
                      </Box>

                      {activeWidget.slot.type === "main_grid" && (
                        <FormControl size="small" sx={{ width: 120 }}>
                          <InputLabel>Width</InputLabel>
                          <Select
                            label="Width"
                            value={activeWidget.slot.size.md}
                            disabled={!canEdit}
                            onChange={(e) =>
                              changeSize(
                                activeWidget.id,
                                Number(e.target.value),
                              )
                            }
                          >
                            <MenuItem value={3}>25%</MenuItem>
                            <MenuItem value={4}>33%</MenuItem>
                            <MenuItem value={6}>50%</MenuItem>
                            <MenuItem value={8}>66%</MenuItem>
                            <MenuItem value={12}>100%</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    </Box>
                  </Paper>
                );
              })}
            </Stack>

            {canEdit && (
              <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
                <Button variant="contained" onClick={saveWidgets}>
                  Save Widgets
                </Button>

                <Button variant="outlined" onClick={resetDashboard}>
                  Reset
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">Available Widgets</Typography>

            <Stack spacing={2} sx={{ mt: 2 }}>
              {availableWidgets.map((widget) => (
                <Paper key={widget.id} sx={{ p: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography fontWeight={700}>{widget.title}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {widget.description}
                      </Typography>
                    </Box>

                    {canAdd && (
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => addWidget(widget.id)}
                      >
                        <Add />
                      </IconButton>
                    )}
                  </Box>
                </Paper>
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
